import { z } from 'zod';
import { FormFieldSchema } from '../../conversation-ui/schemas/form.schema';
import { AgentActionSchema } from '../../schemas/agentAction';
/**
 * Workflow Schema
 *
 * Defines the structure for workflows, workflow definitions, and workflow sessions.
 * These are shared types used across backend, frontend, and admin packages.
 */
// Transition types (Section 11 of spec)
export const TransitionTargetSchema = z.union([
    z.string(), // Workflow slug (e.g., "wf.cookAtHome.entry")
    z.object({
        workflow: z.string(),
        step: z.string().optional()
    }), // Specific step
    z.object({
        mode: z.literal('complete')
    }), // End workflow, create plan
    z.object({
        mode: z.literal('agent'),
        instruction: z.string()
    }) // Hand off to agent
]);
export const OtherTransitionSchema = z.object({
    mode: z.enum(['toAgent', 'toWorkflow', 'disabled']),
    prompt: z.string().optional(), // For mode: 'toAgent'
    schema: z.string().optional(), // Expected agent response schema
    mapResult: z.record(TransitionTargetSchema).optional(),
    to: TransitionTargetSchema.optional(), // For mode: 'toWorkflow'
    fallback: TransitionTargetSchema.optional() // Fallback if agent fails
});
export const TransitionDestinationSchema = z.object({
    to: TransitionTargetSchema,
    guard: z.string().optional(), // Optional condition
    seedData: z.record(z.any()).optional() // Data to pass to destination
});
export const StepTransitionSchema = z.object({
    mode: z.enum(['byAnswer', 'byGuard', 'always']),
    map: z.record(TransitionDestinationSchema).optional(), // Map answers to destinations
    elseIf: z
        .array(z.object({
        guard: z.string(), // Expression to evaluate
        to: TransitionTargetSchema
    }))
        .optional(), // Fallback conditions
    default: TransitionTargetSchema.optional(), // Default destination
    onOther: OtherTransitionSchema.optional() // Handle "Other" / free-text responses
});
export const WorkflowLinkSchema = z.object({
    rel: z.enum(['parent', 'sibling', 'child', 'alt', 'reco']),
    to: z.string(),
    label: z.string(),
    guard: z.string().optional(),
    auto: z.string().optional()
});
export const WorkflowStepSchema = z.object({
    id: z.string(),
    aim: z.string(),
    question: z.string(),
    field: FormFieldSchema.optional(), // DEPRECATED: Use fields instead. Single static field (for backward compatibility)
    fields: z.array(FormFieldSchema).optional(), // Multiple static fields - each field has its own id for referencing in plans/transitions
    agentTask: z
        .object({
        instruction: z.string(), // Prompt template
        outputUI: z.enum(['singleChoice', 'multiChoice', 'dateTime', 'textInput', 'number']),
        context: z.array(z.string()) // Step IDs for context
    })
        .optional(), // Agent-generated ConversationUI
    forceAgent: z.boolean(),
    transition: StepTransitionSchema.optional(), // v2 transitions
    links: z.array(WorkflowLinkSchema).optional() // Legacy static links
});
export const PlanStepSchema = z.object({
    id: z.string(),
    title: z.string(), // Display title for the step (e.g., "Choose your meal")
    aim: z.string(),
    why: z.string().optional(),
    from: z.string().optional(), // Reference workflow step (e.g., "steps.ideas")
    createOffline: z
        .object({
        enabled: z.boolean(),
        artifactType: z.string(), // Artifact type: 'Document', 'Checklist', 'Cards', etc.
        name: z.string().optional() // Template: "Recipe for {{plan.pickMeal.selected}}"
    })
        .optional(), // Create artifact offline before/during step execution
    agentTask: z
        .object({
        instruction: z.string(), // Template with {{plan.stepId.value}} variables
        outputType: z.string(), // Artifact type: 'doc' | 'cards' | etc.
        context: z.array(z.string()), // Plan context (e.g., ["plan.pickMeal.selected"])
        agentActions: z.array(AgentActionSchema).optional() // Agent actions to inject into created artifact
    })
        .optional(),
    condition: z.string().optional(),
    complete: z.string().optional() // e.g., "onSelect", "onEdit", "manual"
});
export const WorkflowPlanConfigSchema = z.object({
    title: z.string(),
    steps: z.array(PlanStepSchema),
    agentGenerated: z.boolean().optional() // Default: false (offline)
});
export const WorkflowDefinitionSchema = z.object({
    settings: z.object({
        forceAgentForFlow: z.boolean(),
        offlineRunnable: z.boolean()
    }),
    steps: z.array(WorkflowStepSchema),
    plan: WorkflowPlanConfigSchema.optional(),
    links: z.array(WorkflowLinkSchema)
});
export const WorkflowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    description: z.string().optional(),
    aim: z.string().optional(),
    why: z.string().optional(),
    imageUrl: z.string().optional(),
    userId: z.string(),
    agentId: z.string().optional(),
    projectId: z.string().optional(),
    isPublic: z.boolean(),
    isTemplate: z.boolean(),
    tags: z.array(z.string()).optional(),
    category: z.string().optional(),
    version: z.number(),
    parentWorkflowId: z.string().optional(),
    definition: WorkflowDefinitionSchema,
    metadata: z.record(z.any()),
    status: z.enum(['draft', 'published', 'archived', 'deprecated']),
    createdAt: z.string(),
    updatedAt: z.string(),
    publishedAt: z.string().optional(),
    deprecatedAt: z.string().optional(),
    usageCount: z.number(),
    completionRate: z.number().optional(),
    avgDurationSeconds: z.number().optional()
});
export const WorkflowSessionSchema = z.object({
    id: z.string(),
    workflowId: z.string(),
    workflowVersion: z.number(),
    conversationId: z.string(),
    userId: z.string(),
    agentId: z.string().optional(),
    status: z.enum(['active', 'completed', 'abandoned']),
    answers: z.record(z.any()),
    currentStepIndex: z.number(),
    currentStepId: z.string(),
    startedAt: z.string(),
    completedAt: z.string().optional(),
    planId: z.string().optional()
});
//# sourceMappingURL=workflow.schema.js.map